/**
 * Event radar — which events a trainer sees, and what their top friend still
 * needs from them.
 *
 * Pure and storage-agnostic so it is unit-testable without a DOM or the network;
 * the component wires the queries. Mirrors the raidDexCrossRef.ts pattern.
 *
 * WHY THIS EXISTS
 * ---------------
 * ProfilePriorityWidgets gated on `playsRaids || playsPvp` and returned null
 * otherwise, so a collector / trader / casual trainer saw NO priority widget at
 * all — the questionnaire asked what they care about and then the profile
 * ignored three of the five answers. The radar now picks its event categories
 * from the trainer's play styles, so everyone gets a block about their game.
 */
import type { PlayStyle } from "./userPriorities";

/**
 * Categories where the point is catching things — a spawn-heavy event is where
 * a shiny / XXL / lucky candidate actually drops. Shared by every style that is
 * fundamentally about collecting: a trader needs fodder and a casual player is
 * still catching, so they get the same view as a collector.
 */
const CATCH_CATEGORIES = ["community-day", "spotlight", "go-fest", "event"] as const;

/**
 * Event categories to surface per play style.
 *
 * `pvp` is deliberately empty: PvpSeasonPanel already owns league events and renders
 * its own season progress, so duplicating them here would show a PvP trainer the
 * same event twice.
 */
const CATEGORIES_BY_STYLE: Readonly<Record<PlayStyle, readonly string[]>> = {
  raids: ["raid", "raid-day", "raid-hour", "max-battle", "max-monday"],
  collector: CATCH_CATEGORIES,
  trader: CATCH_CATEGORIES,
  casual: CATCH_CATEGORIES,
  pvp: [],
};

/** The event categories this trainer's radar should show. Empty = no block. */
/**
 * Every category the radar can watch: what an unanswered questionnaire (or a
 * PvP-only trainer, whose styles pick nothing) gets, so the block is on the
 * Profile for everyone (2026-09-04 owner direction).
 */
export const ALL_RADAR_CATEGORIES: ReadonlySet<string> = new Set<string>(
  Object.values(CATEGORIES_BY_STYLE).flat(),
);

export function radarCategoriesFor(
  primary: PlayStyle,
  secondary: PlayStyle | null,
): ReadonlySet<string> {
  const out = new Set<string>();
  for (const style of [primary, secondary]) {
    if (!style) continue;
    for (const c of CATEGORIES_BY_STYLE[style] ?? []) out.add(c);
  }
  return out;
}

/**
 * Block title. The PRIMARY style names the block when it has categories of its
 * own, so a raids-primary trainer reads "Raid radar" even if their secondary
 * style also pulls in catch events.
 */
export function radarTitleFor(primary: PlayStyle, secondary: PlayStyle | null): string {
  const names = (s: PlayStyle | null) => (s === "raids" ? "Raid radar" : "Catch radar");
  if (primary && (CATEGORIES_BY_STYLE[primary] ?? []).length > 0) return names(primary);
  if (secondary && (CATEGORIES_BY_STYLE[secondary] ?? []).length > 0) return names(secondary);
  return "Event radar";
}

/** True when an event belongs on this trainer's radar. */
export function eventMatchesRadar(
  ev: { category?: string; rawType?: string },
  categories: ReadonlySet<string>,
): boolean {
  if (ev.category && categories.has(ev.category)) return true;
  // rawType is LeekDuck's original slug; category is our mapped one. Checking
  // both means a newly-added upstream type still lands somewhere sensible
  // rather than silently vanishing from every radar.
  return !!ev.rawType && categories.has(ev.rawType);
}

/**
 * Variants a friend can be shown as "still needs".
 *
 * /compare emits eight (dexBackfillMapping.DexVariant) but the widget showed
 * four, silently dropping XXL/XXS — so "Kyogre lucky / XXL" was unsayable even
 * though the data was already there. shadow/purified stay out: they are curated
 * rosters most species are not on, and a shadow gap on an ordinary raid boss
 * would be noise rather than a lead.
 */
export const FRIEND_GAP_VARIANTS = [
  "shiny",
  "lucky",
  "perfect",
  "xxl",
  "xxs",
  "pokemon",
] as const;
export type FriendGapVariant = (typeof FRIEND_GAP_VARIANTS)[number];

const GAP_VARIANT_SET: ReadonlySet<string> = new Set(FRIEND_GAP_VARIANTS);

/** Short badge label for a gap variant. */
export function friendGapLabel(v: FriendGapVariant): string {
  switch (v) {
    case "pokemon": return "Dex";
    case "shiny": return "Shiny";
    case "lucky": return "Lucky";
    case "perfect": return "100%";
    case "xxl": return "XXL";
    case "xxs": return "XXS";
  }
}

export interface FriendDexTotal {
  friendId: number;
  /** Caught total from the friend's dex summary; null when it has not resolved. */
  total: number | null;
}

/**
 * The "top friend" — the one with the most complete dex.
 *
 * Ties break on the lower id so the choice is stable across renders rather than
 * flickering between two equally-complete friends. Friends whose summary has not
 * resolved (null) are skipped, never treated as zero — a slow request must not
 * make someone look like they have an empty dex.
 */
export function pickTopFriend(entries: readonly FriendDexTotal[]): number | null {
  let best: FriendDexTotal | null = null;
  for (const e of entries) {
    if (e.total == null) continue;
    if (!best || e.total > best.total! || (e.total === best.total && e.friendId < best.friendId)) {
      best = e;
    }
  }
  return best?.friendId ?? null;
}

export interface EventSpawnInput {
  speciesId?: number;
  name?: string;
  shinyBoost?: boolean;
  /**
   * Explicit variants this species applies in, from an admin event-link
   * override — any of the app's dex variants (mega / shadow / gmax / etc.). Only
   * the TRADEABLE ones (pokemon / shiny / lucky) can ever produce a trade, so a
   * mega- or shadow-only link yields no trade here (it surfaces on the trainer's
   * own gap radar instead). When present, shiny is eligible iff it's listed,
   * instead of the shinyBoost-derived default. Absent for auto-detected spawns.
   */
  variants?: string[];
}
export interface CompareRow {
  variant: string;
  speciesId: number;
}
export interface FriendEventGap {
  speciesId: number;
  variant: FriendGapVariant;
}

/**
 * What the friend still needs from THIS event's spawns.
 *
 * `iHave` on /compare means "I own it, they don't", so an event species landing
 * there is exactly that friend chasing it.
 *
 * Ordered by spawn order, then by FRIEND_GAP_VARIANTS (rarest hunt first), and
 * capped — the strip shows a few leads, not a matrix. The cap is the caller's so
 * the UI decides how much room it has.
 */
export function friendEventGaps(
  spawns: readonly EventSpawnInput[],
  iHave: readonly CompareRow[],
  limit: number,
): FriendEventGap[] {
  const needed = new Map<number, Set<string>>();
  for (const row of iHave) {
    if (!GAP_VARIANT_SET.has(row.variant)) continue;
    let set = needed.get(row.speciesId);
    if (!set) needed.set(row.speciesId, (set = new Set()));
    set.add(row.variant);
  }

  const out: FriendEventGap[] = [];
  const seen = new Set<string>();
  for (const sp of spawns) {
    const id = sp.speciesId;
    if (typeof id !== "number" || !Number.isFinite(id) || id < 1) continue;
    const set = needed.get(id);
    if (!set) continue;
    for (const variant of FRIEND_GAP_VARIANTS) {
      if (!set.has(variant)) continue;
      const key = `${id}:${variant}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ speciesId: id, variant });
      if (out.length >= limit) return out;
    }
  }
  return out;
}

/**
 * What the friend still needs from THIS event's spawns, computed from the
 * friend's FULL dex (GET /api/friends/:id/dex `rows`) rather than the /compare
 * diff.
 *
 * The old path keyed off /compare `iHave` — "species I own that they don't" —
 * so a raid legendary the friend is missing could only surface if the VIEWER
 * already owned it. For a fresh raid boss (Kyogre) that neither trainer has yet,
 * that meant the strip said "already has everything" when the friend plainly
 * needs it. Reading the friend's own dex fixes that: a spawn the friend has no
 * present row for is a genuine Dex gap regardless of what the viewer owns.
 *
 *   - Base "pokemon" gap: the friend has no present row for the species at all.
 *   - "shiny" gap: the friend owns the species but not a shiny of it, and the
 *     spawn is shiny-eligible (`shinyBoost`). Kept narrow on purpose — luck-based
 *     variants (lucky/xxl/xxs/perfect) would be noise on a raid strip.
 *
 * Ordered by spawn order, then Dex-gap before shiny-gap, and capped by the
 * caller so the UI decides how many leads it has room for.
 */
export function friendEventGapsFromDex(
  spawns: readonly EventSpawnInput[],
  friendRows: readonly CompareRow[],
  limit: number,
): FriendEventGap[] {
  const ownedBase = new Set<number>();
  const ownedShiny = new Set<number>();
  for (const r of friendRows) {
    ownedBase.add(r.speciesId); // any present variant implies base ownership
    if (r.variant === "shiny") ownedShiny.add(r.speciesId);
  }

  const out: FriendEventGap[] = [];
  const seen = new Set<number>();
  for (const sp of spawns) {
    const id = sp.speciesId;
    if (typeof id !== "number" || !Number.isFinite(id) || id < 1) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    if (!ownedBase.has(id)) {
      out.push({ speciesId: id, variant: "pokemon" });
    } else if (sp.shinyBoost && !ownedShiny.has(id)) {
      out.push({ speciesId: id, variant: "shiny" });
    } else {
      continue;
    }
    if (out.length >= limit) return out;
  }
  return out;
}

/**
 * Profile "trades to chase" — the variants a friend can be surfaced as still
 * missing from an event's spawns.
 *
 * Deliberately just Shiny / Lucky / Main: the profile strip is a "what should I
 * chase at this event to bank a friend trade" nudge, and the size / IV
 * refinements (XXL / XXS / Perfect) are luck-of-the-catch noise that drowned out
 * the three that actually motivate playing an event. The order still matters —
 * it is how the POOL of candidates is ranked before the launch-random pick (see
 * bestFriendTrades + pickRandomTrades) — so Shiny leads, then Lucky, then Main.
 */
export const TRADE_PRIORITY_VARIANTS = [
  "shiny",
  "lucky",
  "pokemon",
] as const;

/** A friend's dex rows, tagged with who they belong to (for the trade strip). */
export interface FriendDexRows {
  friendId: number;
  friendName: string;
  rows: readonly CompareRow[];
}

/** One surfaced trade: a friend and the species/variant they still need. */
export interface FriendTrade {
  friendId: number;
  friendName: string;
  speciesId: number;
  variant: FriendGapVariant;
}

/**
 * The single highest-priority variant a friend is missing for `speciesId`, or
 * null when they need nothing tradeable from it. Walks TRADE_PRIORITY_VARIANTS
 * and returns the first that is available AND missing, under these rules:
 *   - `isAvailable` gates every variant on the GO availability layer, so an
 *     unreleased shiny (or any variant a species cannot carry) never surfaces.
 *   - Shiny only counts when the spawn is shiny-eligible — the point is to catch
 *     that shiny AT the event and bank it for the trade.
 *   - Base (Main) and Shiny do not require prior ownership: a single trade of a
 *     caught copy fills them. Lucky is a REFINEMENT of a species the friend
 *     already owns, so if they own no base the meaningful gap is Main, not Lucky.
 */
function firstMissingTradeVariant(
  speciesId: number,
  shinyEligible: boolean,
  ownedBase: ReadonlySet<number>,
  ownedByVariant: ReadonlySet<string>,
  isAvailable: (variant: FriendGapVariant, speciesId: number) => boolean,
  allowed?: ReadonlySet<string>,
): FriendGapVariant | null {
  for (const variant of TRADE_PRIORITY_VARIANTS) {
    // An admin override restricts the event to specific variants — never offer a
    // trade in a variant the curator didn't list for this species.
    if (allowed && !allowed.has(variant)) continue;
    if (!isAvailable(variant, speciesId)) continue;
    if (variant === "shiny") {
      if (!shinyEligible) continue;
      if (!ownedByVariant.has(`${speciesId}:shiny`)) return "shiny";
      continue;
    }
    if (variant === "pokemon") {
      if (!ownedBase.has(speciesId)) return "pokemon";
      continue;
    }
    // lucky — a refinement of a species the friend already owns.
    if (!ownedBase.has(speciesId)) continue;
    if (!ownedByVariant.has(`${speciesId}:${variant}`)) return variant;
  }
  return null;
}

/**
 * The best `limit` trade opportunities across ALL friends for this event's
 * spawns — the ranked POOL the profile "Trades to chase" strip draws from.
 *
 * Replaces the old top-friend model (surface what the single most-complete
 * friend needs) with: for every spawning species and every friend, the friend's
 * highest-priority available missing variant (see firstMissingTradeVariant,
 * Shiny → Lucky → Main) is a candidate trade. Candidates rank by variant
 * priority, then spawn order, then friendId (stable), are deduped to one per
 * friend-species, and capped at `limit`. A species can appear for more than one
 * friend — each is a separate "catch it here, trade it later" opportunity.
 *
 * The caller passes a generous `limit` (the pool cap, 12) and then draws the
 * three it actually shows with pickRandomTrades, so the strip surfaces a fresh
 * trio of the best leads each app launch rather than the same fixed three.
 */
export function bestFriendTrades(
  spawns: readonly EventSpawnInput[],
  friends: readonly FriendDexRows[],
  isAvailable: (variant: FriendGapVariant, speciesId: number) => boolean,
  limit: number,
): FriendTrade[] {
  // Per-friend ownership: base species set + a "speciesId:variant" present set.
  const owned = friends.map((f) => {
    const base = new Set<number>();
    const byVariant = new Set<string>();
    for (const r of f.rows) {
      base.add(r.speciesId);
      byVariant.add(`${r.speciesId}:${r.variant}`);
    }
    return { friend: f, base, byVariant };
  });

  interface Candidate extends FriendTrade {
    spawnOrder: number;
    priority: number;
  }
  const candidates: Candidate[] = [];
  spawns.forEach((sp, spawnOrder) => {
    const id = sp.speciesId;
    if (typeof id !== "number" || !Number.isFinite(id) || id < 1) return;
    // Admin override: restrict to the curator's variants, and treat shiny as
    // eligible iff they listed it (rather than the auto shinyBoost hint).
    const allowed =
      sp.variants && sp.variants.length > 0 ? new Set(sp.variants) : undefined;
    const shinyEligible = allowed ? allowed.has("shiny") : !!sp.shinyBoost;
    for (const { friend, base, byVariant } of owned) {
      const variant = firstMissingTradeVariant(id, shinyEligible, base, byVariant, isAvailable, allowed);
      if (!variant) continue;
      candidates.push({
        friendId: friend.friendId,
        friendName: friend.friendName,
        speciesId: id,
        variant,
        spawnOrder,
        // firstMissingTradeVariant only ever returns a TRADE_PRIORITY_VARIANTS
        // member, but its declared return is the wider FriendGapVariant, so widen
        // the lookup array's element type for the search.
        priority: (TRADE_PRIORITY_VARIANTS as readonly FriendGapVariant[]).indexOf(variant),
      });
    }
  });

  candidates.sort(
    (a, b) =>
      a.priority - b.priority ||
      a.spawnOrder - b.spawnOrder ||
      a.friendId - b.friendId,
  );

  const out: FriendTrade[] = [];
  const seen = new Set<string>();
  for (const c of candidates) {
    const key = `${c.friendId}:${c.speciesId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      friendId: c.friendId,
      friendName: c.friendName,
      speciesId: c.speciesId,
      variant: c.variant,
    });
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Draw `count` trades from a ranked pool, shuffled deterministically by `seed`.
 *
 * The profile strip shows three leads, but always showing the top three (Shiny
 * Kyogre for Ash, Shiny Kyogre for Misty, …) makes the strip feel static and
 * over-weights the same species/friends. So bestFriendTrades hands back a pool
 * of up to 12 and this picks three of them, seeded by a per-launch value
 * (lib/launchSeed): the trio is STABLE within one app launch (no reshuffle on
 * every profile re-render) and re-randomised on the next launch.
 *
 * Pure + seed-deterministic (mulberry32 → Fisher–Yates) so it is unit-testable
 * and the same seed always yields the same pick. When the pool is already at or
 * below `count`, it is returned in rank order unchanged.
 */
export function pickRandomTrades<T>(
  pool: readonly T[],
  seed: number,
  count: number,
): T[] {
  if (pool.length <= count) return pool.slice();
  // mulberry32: a tiny, well-distributed 32-bit PRNG. Seeded, so deterministic.
  let s = seed >>> 0;
  const rand = () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const arr = pool.slice();
  // Fisher–Yates over the whole pool, then take the first `count`.
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr.slice(0, count);
}

/**
 * "needs Kyogre Lucky and XXL" — the prose under a friend's name.
 * Groups by species so one species with three gaps reads as one clause.
 */
export function describeGaps(
  gaps: readonly FriendEventGap[],
  speciesName: (id: number) => string | undefined,
): string {
  const byId = new Map<number, FriendGapVariant[]>();
  for (const g of gaps) {
    const list = byId.get(g.speciesId);
    if (list) list.push(g.variant);
    else byId.set(g.speciesId, [g.variant]);
  }
  const clauses: string[] = [];
  // Array.from, not a bare Map iteration: the client tsconfig targets below
  // es2015 and cannot for..of a Map directly.
  for (const [id, variants] of Array.from(byId.entries())) {
    const labels = variants.map(friendGapLabel);
    const joined =
      labels.length === 1
        ? labels[0]
        : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
    clauses.push(`${speciesName(id) ?? `#${id}`} ${joined}`);
  }
  return clauses.join(", ");
}
